import { createSignal } from 'solid-js'

// Keep in sync with the pre-paint script in index.html, which applies the
// same stored-or-OS theme before the bundle loads to avoid a dark flash on paper.
const COLOR_THEME_STORAGE_KEY = 'colorTheme'
export const COLOR_THEMES = ['midnight', 'paper'] as const
const THEME_COLOR_META: Record<ColorTheme, string> = {
   midnight: '#1e293b',
   paper: '#f4f1ea',
}
const LIGHT_SCHEME_QUERY = '(prefers-color-scheme: light)'

export type ColorTheme = (typeof COLOR_THEMES)[number]
/** 'system' (nothing stored) follows the OS light/dark setting. */
export type ColorThemePreference = 'system' | ColorTheme

/**
 * Reads the explicitly chosen theme; storage can throw in private mode or when blocked.
 *
 * @returns The stored theme, or 'system' when the user has not picked one.
 */
function loadStoredPreference(): ColorThemePreference {
   try {
      const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY)
      return COLOR_THEMES.includes(stored as ColorTheme) ?
            (stored as ColorTheme)
         :  'system'
   } catch {
      return 'system'
   }
}

const lightSchemeMedia =
   typeof window !== 'undefined' && typeof window.matchMedia === 'function' ?
      window.matchMedia(LIGHT_SCHEME_QUERY)
   :  null

const [colorThemePreference, setColorThemePreference] =
   createSignal<ColorThemePreference>(loadStoredPreference())
const [prefersLightScheme, setPrefersLightScheme] = createSignal(
   lightSchemeMedia?.matches ?? false,
)

lightSchemeMedia?.addEventListener('change', event =>
   setPrefersLightScheme(event.matches),
)

/**
 * The theme actually applied: the user's pick, or the OS preference under 'system'.
 *
 * @returns The active color theme.
 */
export const colorTheme = (): ColorTheme => {
   const preference = colorThemePreference()
   if (preference !== 'system') {
      return preference
   }
   return prefersLightScheme() ? 'paper' : 'midnight'
}

/**
 * Selects and persists a theme preference; 'system' clears the stored key.
 *
 * @param preference - The preference to select.
 */
export function selectColorTheme(preference: ColorThemePreference) {
   setColorThemePreference(preference)
   try {
      if (preference === 'system') {
         localStorage.removeItem(COLOR_THEME_STORAGE_KEY)
      } else {
         localStorage.setItem(COLOR_THEME_STORAGE_KEY, preference)
      }
   } catch {
      // Preference just won't survive a reload.
   }
}

/**
 * Applies a color theme to the document root and the browser chrome color.
 *
 * @param theme - The theme to apply.
 */
export function applyColorThemeToDocument(theme: ColorTheme) {
   if (typeof document === 'undefined') {
      return
   }
   document.documentElement.dataset.theme = theme
   document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLOR_META[theme])
}

/**
 * Reads a CSS custom property from the document root, for canvas drawing.
 *
 * @param name - The custom property name, including the leading dashes.
 * @param fallback - Value used when the property is unset.
 * @returns The resolved value.
 */
export function readThemeColor(name: string, fallback: string) {
   if (typeof document === 'undefined') {
      return fallback
   }
   const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
   return value || fallback
}
